import { useCallback, useEffect, useState } from "react";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { FieldError, INPUT_CLASS, OnlineShell, Panel } from "@/components/online/OnlineShell";
import { errorText, listFeedback } from "@/lib/api";

// Private page for reading feedback. The key is FEEDBACK_VIEW_KEY on the server; it is kept
// for this browser tab only (sessionStorage).
const KEY_STORE = "porosity_feedback_key";
const readKey = () => {
  try {
    return sessionStorage.getItem(KEY_STORE) || "";
  } catch {
    return "";
  }
};
const writeKey = (k) => {
  try {
    if (k) sessionStorage.setItem(KEY_STORE, k);
    else sessionStorage.removeItem(KEY_STORE);
  } catch {
    /* storage blocked: the key just isn't remembered */
  }
};

const sourceText = (e) => (e.source === "Other" && e.source_other ? `Other: ${e.source_other}` : e.source || "");
const placeText = (e) => [e.city, e.country].filter(Boolean).join(", ");
const when = (iso) => new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`).toLocaleString();

function downloadCsv(entries) {
  const cols = [["Date", (e) => e.created_at], ["Name", (e) => e.name], ["City", (e) => e.city], ["Country", (e) => e.country], ["Email", (e) => e.email], ["Heard from", sourceText], ["Feedback", (e) => e.message]];
  const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.map(([h]) => cell(h)).join(","), ...entries.map((e) => cols.map(([, f]) => cell(f(e))).join(","))].join("\r\n");
  const url = URL.createObjectURL(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `porosity-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function KeyForm({ onKey, error }) {
  const [value, setValue] = useState("");
  return (
    <Panel testId="feedback-key-panel">
      <form onSubmit={(e) => { e.preventDefault(); if (value.trim()) onKey(value.trim()); }}>
        <div className="label-kicker text-[color:var(--ink-3)] mb-4">Inbox key</div>
        <div className="flex gap-3">
          <Input data-testid="feedback-key-input" type="password" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Key" autoComplete="current-password" className={INPUT_CLASS} autoFocus />
          <button type="submit" className="btn-tactile shrink-0" data-testid="feedback-key-btn">Open</button>
        </div>
        <FieldError testId="feedback-key-error">{error}</FieldError>
      </form>
    </Panel>
  );
}

function Entry({ e }) {
  const meta = [e.name, placeText(e), sourceText(e) && `Heard from ${sourceText(e)}`].filter(Boolean);
  return (
    <li data-testid="feedback-entry">
      <Panel className="!p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <span className="text-[14px] font-semibold text-[color:var(--ink)]">{meta.length ? meta.join(" · ") : "Anonymous"}</span>
          <span className="text-[12px] text-[color:var(--ink-4)] font-coord">{when(e.created_at)}</span>
        </div>
        {e.email && (
          <a href={`mailto:${e.email}`} className="text-[13px] text-[color:var(--sea)] break-all">{e.email}</a>
        )}
        <p className="mt-3 text-[14px] leading-relaxed whitespace-pre-wrap break-words text-[color:var(--ink-2)]">{e.message}</p>
      </Panel>
    </li>
  );
}

export default function FeedbackInboxScreen() {
  const [key, setKey] = useState(readKey);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(() => !!readKey());

  const load = useCallback(async (k) => {
    setBusy(true);
    setError("");
    try {
      setEntries(await listFeedback(k));
      writeKey(k);
    } catch (err) {
      setEntries(null);
      setKey("");
      writeKey("");
      setError(errorText(err, "Could not load feedback"));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const signOut = () => {
    writeKey("");
    setKey("");
    setEntries(null);
  };

  return (
    <OnlineShell testId="feedback-inbox-screen" kicker="Feedback" title="Feedback inbox" intro={entries ? `${entries.length} ${entries.length === 1 ? "entry" : "entries"}, newest first.` : "Enter the inbox key to read what players sent."}>
      {!key || !entries ? (
        key && busy ? <p className="text-[color:var(--ink-3)]">Loading…</p> : <KeyForm onKey={setKey} error={error} />
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4 -ml-3">
            <button type="button" className="btn-ghost" onClick={() => load(key)} disabled={busy} data-testid="feedback-refresh-btn">
              <RefreshCw size={16} strokeWidth={1.8} /> Refresh
            </button>
            <button type="button" className="btn-ghost" onClick={() => downloadCsv(entries)} disabled={!entries.length} data-testid="feedback-csv-btn">
              <Download size={16} strokeWidth={1.8} /> Download CSV
            </button>
            <button type="button" className="btn-ghost" onClick={signOut} data-testid="feedback-signout-btn">
              <LogOut size={16} strokeWidth={1.8} /> Lock
            </button>
          </div>
          {entries.length ? (
            <ul className="space-y-3" data-testid="feedback-list">
              {entries.map((e) => <Entry key={e.id} e={e} />)}
            </ul>
          ) : (
            <Panel testId="feedback-empty"><p className="text-[color:var(--ink-3)]">No feedback yet.</p></Panel>
          )}
        </>
      )}
    </OnlineShell>
  );
}
